import { isToolInputContent } from '@magicschool/business-logic/chat';
import type { FileMetadata, SafeToolInfo } from '@magicschool/business-logic/tools';
import { IMAGE_TOOL_IDS } from '@magicschool/business-logic/tools/constants';
import { logger } from '@magicschool/logger';
import type { Tables } from '@magicschool/supabase/types';
import type { CreateModerationResponse } from 'app/api/moderation/route';
import imageCompression from 'browser-image-compression';
import {
  IMAGE_LOADING_MESSAGE,
  makeBasicRequestMessage,
  makeBasicResponseMessage,
  makeToolInputMessage,
  makeToolOutputMessage,
} from 'features/assistant/messages';
import type { AssistantData, AssistantThread, ImageUrlMap, RawImageUrlMap, SignedImageUrl, ThreadMessage } from 'features/assistant/types';
import { convertDataUrlToImageFile, makeImageMessage, makeUserImageMessage } from 'features/imageGeneration/util';
import { generateMessageSuggestions } from 'features/model-interaction/api';
import { useReadStream } from 'features/model-interaction/hooks/useReadStream';
import type { ChatAction, ChatMessage, Message, PromptStarter } from 'features/model-interaction/types';
import { useStore } from 'features/store';
import { createStoreSlice, type SetField } from 'features/store/zustand';
import { applyGradeLevel } from 'features/tools/api';
import { toolPromptStarters } from 'features/tools/promptStarters';
import { useEffect, useRef, useState } from 'react';
import { ImmutableMap } from 'util/immutable';
import type { CreateAssistantThreadMessageResponse } from '@/app/api/assistant-thread-messages/route';
import type { AssistantThreadDetailsResponse } from '@/app/api/assistant-threads/[id]/route';
import type { CreateAssistantThreadRequestBody, CreateAssistantThreadResponse } from '@/app/api/assistant-threads/route';
import type { DescribeImageResponse } from '@/app/api/describe-image/route';
import type { AssistantThreadExemplarResponse } from '@/app/api/tools/[slugOrId]/assistant-thread-exemplars/route';
import type { FormBuilderValues } from '@/components/FormBuilder/FormBuilder';
import { detectFlaggedWords } from '../moderation/utils';
import { defaultPromptStarters } from './constants';

type AssistantThreadMessage = Tables<'assistant_thread_messages'>;

/**
 * Deduplicates tool_output messages.
 * Keeps the message with the highest index when duplicates are found.
 */
function deduplicateToolOutputMessages(messages: ChatMessage[]): ChatMessage[] {
  let lastToolOutputIndex = -1;

  // Find the index of the last tool_output message
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].content?.[0]?.type === 'tool_output') {
      lastToolOutputIndex = i;
      break;
    }
  }

  // If no tool_output messages found, return original array
  if (lastToolOutputIndex === -1) {
    return messages;
  }

  // Filter out all tool_output messages except the last one
  return messages.filter((message, index) => {
    if (message.content?.[0]?.type === 'tool_output') {
      return index === lastToolOutputIndex;
    }
    return true;
  });
}

type OnPublishCleanupArgs = {
  threadId: number;
  toolDetails: SafeToolInfo;
  description: string;
};

export type ChatStore = {
  setField: SetField<ChatStore>;
  thread?: AssistantThread;
  threadId?: number;
  shareToken?: string;
  messages: ChatMessage[];
  actions: ChatAction[];
  promptStarters: PromptStarter[];
  modifiers: string[];
  followupSuggestions: string[];
  showActions: boolean;
  locked: boolean;
  loading: boolean;
  disabledInput: boolean;
  isStudent: boolean;
  alwaysShowMessageActions: boolean;
  shareDialogOpen: boolean;
  inputsCollapsed: boolean;
  toolDetails: SafeToolInfo | null;
  exemplar: Tables<'assistant_thread_exemplars'> | null;
  inputsKey: number;
  deleteMessageModalOpen: boolean;
  messageToDelete: number | null;
  imageUrlMap: ImageUrlMap;
  fieldsCurrentlyLoading: string[];
  roomToolId: string;
  chatInputValue: string;
  initialized: boolean;
  webSearchEnabled: boolean;
  loadExemplars: (toolId: string) => Promise<void>;
  loadThread: (threadId: number | AssistantThread, prependedMessages?: ChatMessage[]) => Promise<AssistantThread>;
  startNewThread: (params: {
    tool_uuid: string;
    assistantData: AssistantData;
    title?: string;
    customizationId?: string | null;
    preview_mode?: 'custom_tool' | 'room_tool' | 'admin_playground' | null;
    shouldClearThread?: boolean; // default TRUE
  }) => Promise<number>;
  clearThread: () => void;
  reloadMessages: (threadId: number) => void;
  startDeleteMessage: (messageId: number) => void;
  setWebSearchEnabled: (webSearchEnabled: boolean) => void;
  deleteMessage: (onDelete?: (messageId: number) => void) => () => Promise<void>;
  clearChatStore: () => void;
  clearInputs: () => void;
  inputs: FormBuilderValues;
  loadThreadWithMessages: (threadId: number) => Promise<AssistantThread>;
  bootstrap: (toolDetails: SafeToolInfo) => void;
  load: (loadOptions: {
    threadId: number | undefined;
    toolDetails?: SafeToolInfo;
    isStudent?: boolean;
    isRaina?: boolean;
    shareToken?: string;
    rainaIntro?: ChatMessage[];
    currentRoom?: Tables<'rooms'> | null;
    roomToolId?: string;
    initialSuggestions?: string[];
  }) => Promise<{ messages: ChatMessage[]; inputs: FormBuilderValues }>;
  saveChatInput: (args: { userId: string; threadId: number; input: string; files?: FileMetadata[] }) => Promise<AssistantThreadMessage>;
  saveChatOutput: (args: {
    model: string;
    userId: string;
    threadId: number;
    output: string;
    tool_config_id: string;
    generationId?: string;
  }) => Promise<AssistantThreadMessage>;
  saveChatCompletion: (args: {
    model: string;
    userId: string;
    threadId: number;
    input: string;
    output: string;
    files?: FileMetadata[];
    tool_config_id: string;
    generationId?: string;
  }) => Promise<CreateAssistantThreadMessageResponse>;
  saveMessages: (messages: ChatMessage[]) => Promise<CreateAssistantThreadMessageResponse>;
  addLocalMessages: (message: ChatMessage) => void;
  saveToolInput: (args: { userId: string; toolSlug: string; threadId: number; inputs: any }) => Promise<AssistantThreadMessage>;
  saveToolOutput: (args: {
    model: string;
    userId: string;
    toolSlug: string;
    threadId: number;
    output: any;
    tool_config_id: string;
    generationId?: string;
  }) => Promise<AssistantThreadMessage>;
  saveToolGeneration: (args: {
    model: string;
    userId: string;
    toolSlug: string;
    threadId: number;
    inputs: any;
    output: any;
    tool_config_id: string;
    generationId?: string;
  }) => Promise<CreateAssistantThreadMessageResponse>;
  describeImage: (args: { dataUrl: string }) => Promise<string>;
  saveImageGeneration: (args: {
    userId: string;
    threadId: number;
    toolSlug: string;
    fileId: string;
    prompt: string;
    dataUrl: string;
    mode: 'generate' | 'edit';
    type?: string;
    previousResourceId?: string;
  }) => Promise<CreateAssistantThreadMessageResponse>;
  onPublishInitialize: () => void;
  onPublishCleanup: (args: OnPublishCleanupArgs) => Promise<void>;
  sendMessageToModeration: (
    roomId: string,
    threadId: number,
    messageId: number,
    input: string,
    flaggedKeywords: string[],
    toolSlug: string,
  ) => Promise<CreateModerationResponse>;
  loadShareToken: (shareToken: string | undefined) => Promise<void>;
  setMessages: (messages: ChatMessage[] | ((prevMessages: ChatMessage[]) => ChatMessage[])) => void;
};

const defaultState = {
  thread: undefined,
  threadId: undefined,
  shareToken: undefined,
  messages: [],
  actions: [],
  promptStarters: [],
  modifiers: [],
  followupSuggestions: [],
  showActions: true,
  locked: false,
  loading: false,
  disabledInput: false,
  alwaysShowMessageActions: false,
  webSearchEnabled: false,
  inputsCollapsed: false,
  toolDetails: null,
  exemplar: null,
  inputs: {},
  inputsKey: 0,
  shareDialogOpen: false,
  isStudent: false,
  deleteMessageModalOpen: false,
  messageToDelete: null,
  imageUrlMap: ImmutableMap<string, SignedImageUrl>(),
  fieldsCurrentlyLoading: [],
  roomToolId: '',
  chatInputValue: '',
  initialized: false,
};

export const createChatStoreSlice = createStoreSlice('ChatStoreData', defaultState, ({ set, get, setField }) => ({
  setField,
  bootstrap: (toolDetails: SafeToolInfo) => {
    get().loadExemplars(toolDetails.tool.id);
    set({
      toolDetails,
      promptStarters: toolDetails
        ? [...(toolPromptStarters[toolDetails.tool.id] || []), ...defaultPromptStarters]
        : [...defaultPromptStarters],
    });
  },
  loadExemplars: async (toolId: string) => {
    if (!toolId) return;

    await fetch<AssistantThreadExemplarResponse>(`/api/tools/${toolId}/assistant-thread-exemplars`, {
      onSuccess: async ({ response }) => {
        if (response.status === 204) {
          set({ exemplar: null, shareToken: undefined });
          return;
        }
        const exemplar = await response.json();
        set({ exemplar, shareToken: exemplar?.assistant_thread_links_id || undefined });
      },
      responseErrorHandlers: {
        notFound: () => {
          logger.info('ToolId is set but no Exemplar found for toolId', toolId);
        },
        forbidden: () => ({ shortCircuit: true }),
        unauthorized: () => ({ shortCircuit: true }),
      },
    });
  },
  loadShareToken: async (shareToken: string | undefined) => {
    if (!shareToken) return;
    // note: this type isn't super type safe.
    const response = await fetch<{ thread: AssistantThread; imageUrlMap: RawImageUrlMap }>(`/api/links/assistant-threads/${shareToken}`);
    if (response.status === 200) {
      const { thread, imageUrlMap } = await response.json();
      const initThread = await get().loadThread(thread);
      set((s) => {
        let inputs = {};
        if (isToolInputContent(initThread.assistant_thread_messages[0]?.content[0])) {
          inputs = initThread.assistant_thread_messages[0]?.content[0]?.inputs;
        }

        return {
          inputsCollapsed: false,
          disabledInput: true,
          inputs,
          inputsKey: s.inputsKey + 1,
          imageUrlMap: ImmutableMap(imageUrlMap),
        };
      });
    }
  },
  load: async ({ isStudent = false, toolDetails = null, threadId, shareToken, currentRoom, roomToolId, initialSuggestions }) => {
    const patchedTool = isStudent ? toolDetails && applyGradeLevel(toolDetails, currentRoom?.grade_level || 'pre-k') : toolDetails;
    set({
      ...structuredClone(defaultState),
      roomToolId: roomToolId || defaultState.roomToolId,
      loading: true,
      toolDetails: patchedTool,
      threadId,
      isStudent: isStudent,
      inputsCollapsed: Boolean(threadId),
      promptStarters: toolDetails
        ? [...(toolPromptStarters[toolDetails.tool.id] || []), ...defaultPromptStarters]
        : [...defaultPromptStarters],
      modifiers: ['translate', 'customPrompts'],
      followupSuggestions: initialSuggestions || [],
      shareToken,
    });

    const { loadThread } = get();

    if (shareToken) {
      await get().loadShareToken(shareToken);
    } else if (threadId) {
      const fullThread = await loadThread(threadId);

      // it is possible to receive a SupabaseError, (i.e. fullThread.error) message when a thread does not exist
      if (!Object.hasOwn(fullThread, 'error')) {
        set((s) => {
          let inputs = {};
          if (isToolInputContent(fullThread.assistant_thread_messages[0]?.content[0])) {
            inputs = fullThread.assistant_thread_messages[0]?.content[0].inputs;
          }

          return {
            inputs,
            inputsCollapsed: true,
            inputsKey: s.inputsKey + 1,
          };
        });
      }
    }

    set({ loading: false, initialized: true });

    if (toolDetails?.tool.id) {
      await get().loadExemplars(toolDetails.tool.id);
    }

    return {
      messages: get().messages,
      inputs: get().inputs,
    };
  },
  clearChatStore: () => set(structuredClone(defaultState)),
  clearThread: () => set({ threadId: undefined, messages: [], actions: [], disabledInput: false, initialized: false }),
  loadThread: async (thread: number | AssistantThread, prependedMessages: ChatMessage[] = []) => {
    // Preserve server turn order: a stopped answer can have a later ID but an earlier timestamp.
    const threadData = typeof thread !== 'object' ? await get().loadThreadWithMessages(thread) : thread;

    // There are weird scenarios where the threadData is not iterable
    // Do we want to notify the user of this through a toast or something?
    let assistantThreadMessages = threadData?.assistant_thread_messages;
    if (!assistantThreadMessages || !Array.isArray(assistantThreadMessages)) {
      logger.error('Error in loadThread, threadData is not iterable', threadData);
      assistantThreadMessages = [];
    }

    const allMessages = [...prependedMessages, ...assistantThreadMessages] as ChatMessage[];
    const deduplicatedMessages = deduplicateToolOutputMessages(allMessages);
    set({
      messages: deduplicatedMessages,
      threadId: threadData.id,
      thread: threadData,
    });
    return threadData;
  },
  startNewThread: async ({ tool_uuid, assistantData, title, customizationId, shouldClearThread = true }) => {
    if (shouldClearThread) {
      const { clearThread } = get();
      clearThread();
    }

    const request: CreateAssistantThreadRequestBody = { assistantData, title, tool_uuid, customizationId };
    const response = await fetch<CreateAssistantThreadResponse>(`/api/assistant-threads`, {
      method: 'POST',
      body: JSON.stringify(request),
    });
    const thread = await response.json();
    return thread.id;
  },
  reloadMessages: async (threadId: number) => {
    const threadData = await get().loadThreadWithMessages(threadId);

    const messages = threadData.assistant_thread_messages as ChatMessage[];

    const uniqueMessages = messages.reduce((acc, message) => {
      if (!acc.some((m) => m.id === message.id)) acc.push(message);
      return acc;
    }, [] as ChatMessage[]);

    get().setMessages(uniqueMessages);
  },
  clearInputs: () => {
    set((s) => ({
      inputs: {},
      inputsKey: s.inputsKey + 1,
      inputsCollapsed: false,
    }));
  },
  loadThreadWithMessages: async (threadId: number) => {
    const response = await fetch<AssistantThreadDetailsResponse>(`/api/assistant-threads/${threadId}`);
    const data = await response.json();

    if (!Object.hasOwn(data, 'error')) {
      const { imageUrlMap, ...thread } = data.thread;

      set({ imageUrlMap: ImmutableMap(imageUrlMap) });
      // There is some problematic typing going on with Assistant Threads, notably Assistant Thread Resources. We should revisit this if we have a moment but for now this will work :/
      return thread as unknown as AssistantThread;
    }

    return data as unknown as AssistantThread;
  },
  saveMessages: async (messages) => {
    const res = await fetch<CreateAssistantThreadMessageResponse>(`/api/assistant-thread-messages`, {
      method: 'POST',
      body: JSON.stringify({ messages }),
    });
    const data = await res.json();
    return data;
  },
  addLocalMessages: (message) => {
    set((s) => {
      const newMessages = [...s.messages, message];
      const deduplicatedMessages = deduplicateToolOutputMessages(newMessages);

      // If deduplication removed the message, don't update state
      if (deduplicatedMessages.length === s.messages.length) {
        return s;
      }

      return {
        messages: deduplicatedMessages,
      };
    });
  },
  saveChatInput: async ({ userId, threadId, input, files }) => {
    const { saveMessages } = get();
    const userMessage = makeBasicRequestMessage(input, userId, threadId, files);
    const [message] = await saveMessages([userMessage]);
    return message;
  },
  saveChatOutput: async ({ model, userId, threadId, output, tool_config_id, generationId }) => {
    const { saveMessages } = get();
    const assistantMessage = makeBasicResponseMessage({ output, model, userId, threadId, tool_config_id, generationId });
    const [message] = await saveMessages([assistantMessage]);
    return message;
  },
  saveChatCompletion: ({ model, userId, threadId, input, output, files, tool_config_id, generationId }) => {
    const { saveMessages } = get();
    const userMessage = makeBasicRequestMessage(input, userId, threadId, files);
    const assistantMessage = makeBasicResponseMessage({ output, model, userId, threadId, tool_config_id, generationId });

    return saveMessages([userMessage, assistantMessage]);
  },
  saveToolInput: async ({ userId, toolSlug, threadId, inputs }) => {
    const { saveMessages } = get();
    const userMessage = makeToolInputMessage(inputs, userId, threadId, toolSlug);
    const [message] = await saveMessages([userMessage]);
    return message;
  },
  saveToolOutput: async ({ model, userId, toolSlug, threadId, output, tool_config_id, generationId }) => {
    const { saveMessages } = get();
    const assistantMessage = makeToolOutputMessage({ output, model, userId, threadId, toolSlug, tool_config_id, generationId });
    const [message] = await saveMessages([assistantMessage]);
    return message;
  },
  saveToolGeneration: ({ model, userId, toolSlug, threadId, inputs, output, tool_config_id, generationId }) => {
    const { saveMessages } = get();
    const userMessage = makeToolInputMessage(inputs, userId, threadId, toolSlug);
    const assistantMessage = makeToolOutputMessage({ output, model, userId, threadId, toolSlug, tool_config_id, generationId });
    return saveMessages([userMessage, assistantMessage]);
  },
  describeImage: async ({ dataUrl }) => {
    const imageFile = convertDataUrlToImageFile(dataUrl, 'image');
    const compressedImageFile = await imageCompression(imageFile, { maxSizeMB: 1, maxWidthOrHeight: 1500 });

    const compressedDataUrl = await convertImageFileToDataURL(compressedImageFile);

    const res = await fetch<DescribeImageResponse>('/api/describe-image', {
      method: 'POST',
      body: JSON.stringify({ image: compressedDataUrl }),
      responseErrorHandlers: { unknown: () => ({ shortCircuit: true }) },
    });

    const json = await res.json();

    if (!res.ok) {
      logger.error('There was an error describing the image', json);
      return "We weren't able to extract meaning from the image. Ask the user to describe the image if they're asking about it.";
    }

    return json.description;
  },
  saveImageGeneration: async ({ userId, threadId, toolSlug, fileId, prompt, type, dataUrl, mode, previousResourceId }) => {
    const { saveMessages, describeImage } = get();

    const description = await describeImage({ dataUrl });

    const userMessage = makeImageMessage({ userId, threadId, resourceId: fileId, prompt, description, previousResourceId });

    // Only need to create a tool input message if we're using the image tool
    if (IMAGE_TOOL_IDS.includes(toolSlug)) {
      // We add a hidden user message for edited images to make the student table work
      const assistantMessage =
        mode === 'edit' ? makeUserImageMessage(userId, threadId) : makeToolInputMessage({ type, prompt }, userId, threadId, toolSlug);
      return saveMessages([userMessage, assistantMessage]);
    }

    return saveMessages([userMessage]);
  },
  onPublishInitialize: () => {
    setField('followupSuggestions')([]);
    setField('loading')(true);
    setField('messages')((messages) => {
      return messages.concat(IMAGE_LOADING_MESSAGE);
    });
  },
  onPublishCleanup: async ({ threadId, toolDetails, description }) => {
    const { reloadMessages } = get();

    if (!toolDetails.tool.roles.includes('student') && !IMAGE_TOOL_IDS.includes(toolDetails.tool.slug)) {
      const context = [{ role: 'assistant', content: description }] as Message[];
      const suggestions = await generateMessageSuggestions({
        context,
        role: toolDetails.tool.roles[0] ?? 'teacher',
        toolSlug: toolDetails.tool.slug,
      });

      setField('followupSuggestions')(suggestions);
    }

    reloadMessages(threadId);
    setField('loading')(false);
  },
  startDeleteMessage: (messageId: number) => {
    set({ deleteMessageModalOpen: true, messageToDelete: messageId });
  },
  deleteMessage: (onDelete) => async () => {
    const { messageToDelete } = get();
    if (!messageToDelete) return;
    logger.debug('deleteMessage', messageToDelete);
    const response = await fetch(`/api/assistant-thread-messages/${messageToDelete}`, { method: 'DELETE' });

    if (response.status === 200) {
      const filteredMessages = get().messages.filter((m) => m.id !== messageToDelete);
      const deduplicatedMessages = deduplicateToolOutputMessages(filteredMessages);
      set({ messages: deduplicatedMessages, deleteMessageModalOpen: false });
      onDelete?.(messageToDelete);
    }
  },
  sendMessageToModeration: async (roomId, threadId, messageId, input, flaggedKeywords, toolSlug) => {
    const flaggedWords = detectFlaggedWords(input, flaggedKeywords);

    // Implement decaying retry with exponential backoff
    const MAX_RETRIES = 3;
    const BASE_DELAY_MS = 1000; // Start with 1s delay

    let error: Error | null = null;
    for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
      try {
        const res = await fetch<CreateModerationResponse & { message?: string }>(`/api/moderation`, {
          method: 'POST',
          body: JSON.stringify({ roomId, threadId, messageId, input, flaggedWords, toolSlug }),
          responseErrorHandlers: {
            // these shouldn't present an error to the user
            badRequest: () => ({ shortCircuit: true }),
            conflict: () => ({ shortCircuit: true }),
            unauthorized: () => ({ shortCircuit: true }),
            forbidden: () => ({ shortCircuit: true }),
            unknown: () => ({ shortCircuit: true }),
          },
        });
        const data = await res.json();
        return data;
      } catch (e) {
        error = e as Error;

        // If this isn't the last attempt, wait before retrying
        if (attempt < MAX_RETRIES - 1) {
          // Calculate delay with exponential backoff
          const delayMs = BASE_DELAY_MS * Math.pow(2, attempt);
          // Add jitter to prevent synchronized retries
          const jitteredDelay = delayMs * (0.8 + Math.random() * 0.4);

          logger.warn(
            `Moderation API request failed (attempt ${attempt + 1}/${MAX_RETRIES}). Retrying in ${Math.round(jitteredDelay)}ms`,
            error,
          );

          // Wait before next retry
          await new Promise((resolve) => setTimeout(resolve, jitteredDelay));
        }
      }
    }

    // If we've exhausted all retries and still failed, throw an error
    logger.error('Failed to send message to moderation after multiple retries', {
      roomId,
      threadId,
      messageId,
      input,
      flaggedWords,
      error,
      retryAttempts: MAX_RETRIES,
    });
    throw new Error('Failed to send message to moderation', { cause: error });
  },
  setWebSearchEnabled: (webSearchEnabled: boolean) => {
    set({ webSearchEnabled });
  },
  setMessages: (messages: ChatMessage[] | ((prevMessages: ChatMessage[]) => ChatMessage[])) => {
    if (typeof messages === 'function') {
      set((state) => {
        const newMessages = messages(state.messages);
        const deduplicatedMessages = deduplicateToolOutputMessages(newMessages);
        return { messages: deduplicatedMessages };
      });
    } else {
      const deduplicatedMessages = deduplicateToolOutputMessages(messages);
      set({ messages: deduplicatedMessages });
    }
  },
}));

// biome-ignore lint/complexity/noBannedTypes: <explanation>
export function useReceiveMessageStream(): [Function, boolean, () => void] {
  const { threadId, setMessages } = useStore(
    ({ ChatStoreData: s }) => ({ threadId: s.threadId, messages: s.messages, setMessages: s.setMessages }),
    [],
  );
  const [readStream, currentOutput, streaming, reset] = useReadStream();
  // biome-ignore lint/complexity/noBannedTypes: <explanation>
  const [messageConstructor, setMessageConstructor] = useState<Function>(() => makeBasicResponseMessage);

  // Use a stable negative ID for the streaming message to distinguish from DB IDs
  const streamingMessageId = useRef(-Date.now());

  useEffect(() => {
    if (currentOutput) {
      setMessages((existing) => {
        // Check if we already have a message that is currently being received
        const receivingMessageIndex = existing.findIndex((m) => m.receiving === true);
        const hasReceivingMessage = receivingMessageIndex !== -1;

        const streamedMessage = {
          id: streamingMessageId.current,
          ...messageConstructor({ output: currentOutput }),
          assistant_thread_id: threadId,
          receiving: streaming ? true : undefined,
        };

        if (hasReceivingMessage) {
          // Update existing streaming message
          const updatedMessages = [...existing];
          updatedMessages[receivingMessageIndex] = streamedMessage;
          return updatedMessages;
        } else {
          // Add new streaming message, removing any special messages
          const filteredMessages = existing.filter(
            (m) => !m.isGeneratingMessage && !m.isThinkingMessage && !m.isSearchingMessage && !m.isProcessingImage,
          );
          return [...filteredMessages, streamedMessage];
        }
      });
    }
  }, [currentOutput, streaming, threadId, setMessages, messageConstructor]);

  const receiveMessageStream = async (stream: ReadableStream, messageConstructor: (output: string) => ThreadMessage) => {
    setMessageConstructor(() => messageConstructor);
    const output = await readStream(stream);
    return output;
  };

  return [receiveMessageStream, streaming, reset];
}

function convertImageFileToDataURL(file: File) {
  return new Promise<string>((resolve) => {
    const reader = new FileReader();
    reader.onload = (event) => resolve(event.target?.result as string);
    reader.readAsDataURL(file);
  });
}
